/**
 * The one action that resolves the current sync status, plus "sync now".
 * Shared by the sync chip, the attention banner and Settings, so the three
 * always offer the same thing for the same state.
 */
import { useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth.tsx'
import { useDB } from '../../hooks/useDB.tsx'
import { useI18n } from '../../lib/i18n/i18n-store.ts'
import { useSyncStatus, type SyncStatus } from '../../lib/sync/sync-status-store.ts'
import { toast } from './toast-store.ts'

export interface SyncAction {
  label: string
  run: () => void
}

/** Statuses that need the user to do something. */
export const ACTIONABLE: ReadonlySet<SyncStatus> = new Set(['needs_auth', 'needs_consent', 'signed_out', 'error'])

export function useSyncActions(): { status: SyncStatus; primary: SyncAction | null; syncNow: () => Promise<void> } {
  const status = useSyncStatus((s) => s.status)
  const { sync } = useDB()
  const { reconnect, grantPermissions, login } = useAuth()
  const { t } = useI18n()

  const syncNow = useCallback(async () => {
    if (!sync) return
    try {
      await sync.syncNow()
      toast.success(t('sync.toast.done'))
    } catch (err) {
      const state = useSyncStatus.getState().status
      // Offline / auth states already explain themselves in the chip and banner.
      if (state === 'error') toast.error(t('sync.toast.failed', { error: err instanceof Error ? err.message : String(err) }))
    }
  }, [sync, t])

  const primary: SyncAction | null =
    status === 'needs_auth' ? { label: t('sync.action.reconnect'), run: reconnect }
    : status === 'needs_consent' ? { label: t('sync.action.grant'), run: () => grantPermissions() }
    : status === 'signed_out' ? { label: t('sync.action.signIn'), run: login }
    : status === 'error' ? { label: t('sync.action.retry'), run: () => void syncNow() }
    : null

  return { status, primary, syncNow }
}
